// components/reusable/AutoSlider.js
import React, { useRef, useEffect, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import Link from "next/link";
import Image from "next/image";

export default function AutoSlider({ posts }) {
  const containerRef = useRef(null);
  const [isHovered, setIsHovered] = useState(false);

  const scroll = direction => {
    const cardWidth = containerRef.current?.firstChild?.getBoundingClientRect().width || 300;
    if (containerRef.current) {
      containerRef.current.scrollBy({
        left: direction === "left" ? -cardWidth - 24 : cardWidth + 24,
        behavior: "smooth",
      });
    }
  };

  useEffect(() => {
    const interval = setInterval(() => {
      if (isHovered || !containerRef.current) return;

      const container = containerRef.current;
      const cardWidth = container.firstChild?.getBoundingClientRect().width || 300;
      const scrollAmount = cardWidth + 24;

      const isAtEnd = container.scrollLeft + container.offsetWidth >= container.scrollWidth - 10;

      if (isAtEnd) {
        container.scrollTo({ left: 0, behavior: "smooth" });
      } else {
        container.scrollBy({ left: scrollAmount, behavior: "smooth" });
      }
    }, 3000);

    return () => clearInterval(interval);
  }, [isHovered]);

  if (!posts?.length) return null;

  return (
    <div className="relative w-full max-w-6xl mx-auto px-4 py-6 text-black cursor-pointer">
      {/* Scroll buttons */}
      <button
        onClick={() => scroll("left")}
        className="absolute left-0 top-1/2 transform -translate-y-1/2 z-10 bg-white shadow-md hover:shadow-lg rounded-full p-2"
      >
        <ChevronLeft />
      </button>

      <div
        ref={containerRef}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        className="flex gap-6 overflow-x-auto no-scrollbar scroll-smooth snap-x snap-mandatory"
      >
        {posts.map((post, index) => (
          <div
            key={post.meta?.slug || index}
            className="snap-start shrink-0 
              w-[100%] sm:w-[calc(50%-12px)] md:w-[calc(33.33%-16px)] lg:w-[calc(25%-18px)] 
              bg-white rounded-2xl shadow-md border hover:shadow-lg 
              transition flex flex-col"
          >
            <Link href={`/blog/${post.meta.slug}`}>
              <div className="relative overflow-hidden rounded-t-2xl h-44">
                <Image
                  src={post.meta.img}
                  alt={post.meta.title}
                  className="w-full h-44 object-cover transform transition-transform duration-300 hover:scale-110"
                  layout="fill"
                  objectFit="cover"
                />
              </div>
            </Link>
            <div className="p-4 flex flex-col justify-between flex-grow">
              <Link href={`/blog/${post.meta.slug}`}>
                <h3 className="text-lg font-semibold mb-2 line-clamp-2 hover:text-blue-600 hover:underline cursor-pointer">{post.meta.title}</h3>
              </Link>
              <Link href={`/blog/${post.meta.slug}`} className="mt-4">
                <span className="flex items-center font-medium text-gray-600 hover:translate-x-1 hover:text-[#f1aa4e] transition">
                  Read More
                  <ChevronRight className="h-4 ml-1" />
                </span>
              </Link>
            </div>
          </div>
        ))}
      </div>

      <button
        onClick={() => scroll("right")}
        className="absolute right-0 top-1/2 transform -translate-y-1/2 z-10 bg-white shadow-md hover:shadow-lg rounded-full p-2"
      >
        <ChevronRight />
      </button>
    </div>
  );
}
